/**
 * Currency detection utility based on user's location
 */

// Supported currencies with their regions
const SUPPORTED_CURRENCIES = {
  'NZ': 'NZD', // New Zealand
  'AU': 'AUD', // Australia
  'US': 'USD', // United States
  'IN': 'INR', // India
  'GB': 'EUR', // United Kingdom (using EUR as specified)
  'UK': 'EUR', // Alternative code for United Kingdom
};

// Default fallback currency
const DEFAULT_CURRENCY = 'USD';

/**
 * Detect user's currency based on their location
 * @returns {Promise<string>} Currency code (NZD, AUD, USD, INR, EUR)
 */
export const detectUserCurrency = async () => {
  try {
    // Try to get location from browser geolocation API
    const position = await getCurrentPosition();
    if (position) {
      const countryCode = await getCountryFromCoordinates(
        position.coords.latitude, 
        position.coords.longitude
      );
      if (countryCode && SUPPORTED_CURRENCIES[countryCode]) {
        return SUPPORTED_CURRENCIES[countryCode];
      }
    }
  } catch (error) {
    console.log('Geolocation failed, trying IP-based detection:', error);
  }

  try {
    // Fallback to IP-based location detection
    const countryCode = await getCountryFromIP();
    if (countryCode && SUPPORTED_CURRENCIES[countryCode]) {
      return SUPPORTED_CURRENCIES[countryCode];
    }
  } catch (error) {
    console.log('IP-based detection failed:', error);
  }

  // Final fallback to browser locale
  try {
    const browserCurrency = getCurrencyFromBrowserLocale();
    if (browserCurrency && Object.values(SUPPORTED_CURRENCIES).includes(browserCurrency)) {
      return browserCurrency;
    }
  } catch (error) {
    console.log('Browser locale detection failed:', error);
  }

  // Return default currency if all methods fail
  return DEFAULT_CURRENCY;
};

/**
 * Get current position using browser geolocation
 * @returns {Promise<GeolocationPosition>}
 */
const getCurrentPosition = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      resolve,
      reject,
      { timeout: 10000, enableHighAccuracy: false }
    );
  });
};

/**
 * Get country code from coordinates using reverse geocoding
 * @param {number} lat Latitude
 * @param {number} lng Longitude
 * @returns {Promise<string>} Country code
 */
const getCountryFromCoordinates = async (lat, lng) => {
  try {
    // Using a free geocoding service (you might want to use a more reliable one)
    const response = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
    );
    const data = await response.json();
    return data.countryCode;
  } catch (error) {
    console.error('Reverse geocoding failed:', error);
    return null;
  }
};

/**
 * Get country code from IP address
 * @returns {Promise<string>} Country code
 */
const getCountryFromIP = async () => {
  try {
    // Using a free IP geolocation service
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    return data.country_code;
  } catch (error) {
    console.error('IP geolocation failed:', error);
    return null;
  }
};

/**
 * Get currency from browser locale
 * @returns {string|null} Currency code
 */
const getCurrencyFromBrowserLocale = () => {
  try {
    const locale = navigator.language || navigator.languages[0];
    
    // Map common locales to our supported currencies
    const localeMap = {
      'en-NZ': 'NZD',
      'en-AU': 'AUD',
      'en-US': 'USD',
      'en-IN': 'INR',
      'en-GB': 'EUR',
      'hi-IN': 'INR',
      'hi': 'INR',
    };

    if (localeMap[locale]) {
      return localeMap[locale];
    }

    // Check for country code in locale
    const countryCode = locale.split('-')[1];
    if (countryCode && SUPPORTED_CURRENCIES[countryCode]) {
      return SUPPORTED_CURRENCIES[countryCode];
    }

    return null;
  } catch (error) {
    console.error('Browser locale detection failed:', error);
    return null;
  }
};

/**
 * Get currency display name
 * @param {string} currencyCode Currency code
 * @returns {string} Display name
 */
export const getCurrencyDisplayName = (currencyCode) => {
  const currencyNames = {
    'NZD': 'New Zealand Dollar',
    'AUD': 'Australian Dollar',
    'USD': 'US Dollar',
    'INR': 'Indian Rupee',
    'EUR': 'Euro'
  };
  return currencyNames[currencyCode] || currencyCode;
};

/**
 * Get country name from currency code
 * @param {string} currencyCode Currency code
 * @returns {string} Country name
 */
export const getCountryFromCurrency = (currencyCode) => {
  const countryMap = {
    'NZD': 'New Zealand',
    'AUD': 'Australia',
    'USD': 'United States',
    'INR': 'India',
    'EUR': 'United Kingdom'
  };
  return countryMap[currencyCode] || 'Unknown';
};

/**
 * Check if currency is supported
 * @param {string} currencyCode Currency code
 * @returns {boolean} Is supported
 */
export const isSupportedCurrency = (currencyCode) => {
  return Object.values(SUPPORTED_CURRENCIES).includes(currencyCode);
};